import * as React from "react"
import {
  Theme,
  ThemeCss,
  ToggleSwitch,
  FormFieldset,
  FormLegend,
  useAriaFormGroupField,
  Heading,
} from "gatsby-interface"
import { visuallyHiddenCss } from "@modules/a11y/stylesheets"
import VisuallyHidden from "@modules/a11y/components/VisuallyHidden"
import { BillingInterval, MachinePricingTier } from "@modules/graphql/types"
import { PlanTierCard } from "./PlanTierCard"
import {
  supportedTiers,
  SupportedPricingTier,
} from "@modules/billing/shared/constants/tiers"
import capitalizeStr from "@modules/toolkit/helpers/capitalizeStr"

const rootCss: ThemeCss = _theme => ({
  display: `flex`,
  flexDirection: `column`,
  alignItems: `center`,
})

const intervalSwitchCss: ThemeCss = theme => ({
  textTransform: `uppercase`,
  fontFamily: theme.fonts.heading,
  marginBottom: theme.space[5],
})

const headingCss: ThemeCss = theme => ({
  fontSize: theme.fontSizes[8],
  fontWeight: theme.fontWeights.extraBold,
  marginBottom: theme.space[7],
})

export const emphesizedWordCss: ThemeCss = theme => ({
  fontWeight: `inherit`,
  color: theme.colors.gatsby,
  background: `-webkit-linear-gradient(${theme.colors.blue[60]}, ${theme.colors.gatsby})`,
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
})

const formFieldCss: ThemeCss = theme => ({
  display: `block`,
  width: `100%`,
})

const tiersCss: ThemeCss = theme => ({
  display: `grid`,
  gridGap: theme.space[7],
})

export type PlanTierSelectorProps = {
  tiers: MachinePricingTier[]
  tierType: `builds` | `hosting`
  selectedTier?: MachinePricingTier | null
  billingInterval: BillingInterval
  setBillingInterval: (val: BillingInterval) => void
  setTier: (val: SupportedPricingTier) => void
  showIntervalToggle?: boolean
  contactSalesLink?: string
  showDetailsLinks?: boolean
  enterpriseSelectable?: boolean
}

export function PlanTierSelector({
  tiers,
  tierType,
  selectedTier,
  setTier,
  billingInterval,
  setBillingInterval,
  showIntervalToggle = true,
  contactSalesLink,
  showDetailsLinks,
  enterpriseSelectable,
}: PlanTierSelectorProps) {
  const {
    getLegendProps,
    getOptionControlProps,
    getOptionLabelProps,
  } = useAriaFormGroupField(`plan`, {
    required: true,
  })

  return (
    <div css={rootCss} id={`${tierType}TierSelector`}>
      {showIntervalToggle && (
        <React.Fragment>
          <VisuallyHidden id="intervalTitle">Billing interval</VisuallyHidden>
          <ToggleSwitch
            id="interval"
            valueOn={BillingInterval.Annual}
            labelOn="Yearly"
            valueOff={BillingInterval.Monthly}
            labelOff="Monthly"
            value={billingInterval}
            onChange={e => {
              const billingInterval = e.target.value as BillingInterval
              setBillingInterval(billingInterval)
            }}
            aria-describedby="intervalTitle"
            tone="SUCCESS"
            css={intervalSwitchCss}
          />
        </React.Fragment>
      )}

      <Heading as="h3" fontVariant="UI" css={headingCss}>
        Select a{" "}
        <strong css={emphesizedWordCss}>
          {capitalizeStr({ str: tierType })}
        </strong>{" "}
        plan
      </Heading>

      <FormFieldset css={formFieldCss}>
        <FormLegend
          {...getLegendProps(`Select a plan`)}
          css={visuallyHiddenCss}
        />
        <div
          css={(theme: Theme) => [
            tiersCss(theme),
            {
              [theme.mediaQueries.desktop]: {
                gridTemplateColumns: `repeat(${tiers.length}, 1fr)`,
              },
            },
          ]}
        >
          {tiers.map(tier => {
            return (
              <PlanTierCard
                key={tier}
                tier={supportedTiers[tier as SupportedPricingTier]}
                tierType={tierType}
                isSelected={tier === selectedTier}
                onSelect={tier => {
                  setTier(tier.pricingTier)
                }}
                getOptionControlProps={getOptionControlProps}
                getOptionLabelProps={getOptionLabelProps}
                billingInterval={billingInterval}
                contactSalesLink={contactSalesLink}
                detailsLink={
                  showDetailsLinks ? `#${tierType}Details` : undefined
                }
                enterpriseSelectable={enterpriseSelectable}
              />
            )
          })}
        </div>
      </FormFieldset>
    </div>
  )
}
